import React, { useContext, useEffect } from "react";
import { PropsWithChildren, createContext, useState } from "react";
import { TAnalyticsPayload, TAppParentMenuItem, TMerchantData, TGraphData, TPaginationParams, TSelectedItem, TTimeZone, MetricsData, TAppSubMenuItem, TPaginationParamsWithLoading } from "../analytics";
import { adjustDateRangeWithCurrentTime, calculateDashArray, calculatePreviousPeriod, combineApiResponseToGraph, convertApiResponseToGraph, getFormattedDateTime, getDateWithMidnight, getStartAndEndOfPreviousYear } from "../AnalyticsHelper";
import { fetchAllGraphData } from "../Druid/DruidServices";
import { NONE, THIS_YEAR_1M, THIS_YEAR_1W, THIS_YEAR_2W, TODAY, LAST_30_DAYS, P1D } from "../AnalyticsConstants";
import { formatDate, formatDateRange, getDateRangeBasedTimeGrain, upperBoundDateformat } from "../MetricsHelper";
import { getISRProjectsForAnalytics } from "Pages/GMS/ISR/Pages/DefineModel/ModelServices/ModalServices";
import { AppContext } from "Context/AppContext";
import moment from "moment";

export const AnalyticsContext = createContext<any>(undefined);

export const AnalyticsContextProvider = ({ children }: PropsWithChildren) => {
    const menuItems = [
        { title: "Compatio", checked: true, isOpen: false, value: "Compatio" }, // Set Compatio as checked by default
        { title: "Bundler", checked: false, isOpen: false, value: "Bundler" },
        { title: "Commend (ARS)", checked: false, isOpen: false, value: "ARS" },
        { title: "Guide", checked: false, isOpen: false, value: "Guide" },
        { title: "SmartBuilder", checked: false, isOpen: false, value: "SmartBuilder" },
        { title: "XCS", checked: false, isOpen: false, value: "XCS" },
        { title: "XRS", checked: false, isOpen: false, value: "XRS" }
    ];

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showGraphLoader, setShowGraphLoader] = useState<boolean>(false);
    const [comingFromDetailPage, setComingFromDetailPage] = useState<boolean>(false);

    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({ pageNumber: 1, pageSize: 30 });

    const [selectedMerchant, setSelectedMerchant] = useState<TMerchantData | undefined>(undefined);
    const [selectedRange, setSelectedRange] = useState<string>('');
    const [dashedArray, setDashedArray] = useState<number[]>([]);

    // Calculate the formatted date range for Last 30 days
    const getInitialDateRangeDisplay = () => {
        const today = moment().tz("UTC");
        const startDate = today.clone().subtract(30, 'days').toDate();
        const endDate = today.toDate();
        return formatDateRange(startDate, endDate, LAST_30_DAYS, "UTC");
    };

    // Initialize with the proper formatted date range as the value
    const [selectedItem, setSelectedItem] = useState<TSelectedItem>({
        label: LAST_30_DAYS,
        value: getInitialDateRangeDisplay()
    });

    const [selectedCompareMenuItem, setSelectedCompareMenuItem] = useState<string>("None");

    // Set Compatio as checked by default in menu data
    const [menuData, setMenuData] = useState<TAppParentMenuItem[]>(menuItems);
    const [selectedTimezone, setSelectedTimezone] = useState<TTimeZone>({ name: "UTC", abbreviation: "UTC" });
    const [merchantData, setMerchantData] = useState<TMerchantData[]>([]);
    const [graphData, setGraphData] = useState<TGraphData[]>([]);
    const [applicationMetrics, setApplicationMetrics] = useState<MetricsData>({ data: [] });
    const [activeRequestCount, setActiveRequestCount] = useState(0);

    // Initialize payload with default values
    const [payload, setPayload] = useState<TAnalyticsPayload>({
        granularity: P1D,
        merchant_key: [],
        dateRange: [],
        compareTo: 'None',
        applications: ["Compatio"], // Set Compatio as the default application
        timezone: 'UTC',
        builder_ids: [],
        box_ids: []
    });

    const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

    const [subMenuItems, setSubMenuItems] = useState<TAppSubMenuItem[]>([]);
    const [selectedSubMenuItems, setSelectedSubMenuItems] = useState<TAppSubMenuItem[]>([]);

    const [commendSubMenuItems, setCommendSubMenuItems] = useState<TAppSubMenuItem[]>([]);
    const [selectedCommendSubMenuItems, setSelectedCommendSubMenuItems] = useState<TAppSubMenuItem[]>([]);

    const ARSBoxData = {
        "1": "Box 1",
        "2": "Box 2",
        "3": "Box 3"
    };

    const [pagination, setPagination] = useState<TPaginationParamsWithLoading>({
        pageNumber: 1,
        pageSize: 10,
        hasMore: false,
        loading: false
    });
    const appContext = useContext(AppContext);
    const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;

    const fetchCommendSubMenuItems = () => {
        const items = appContext.applications.get(appContext?.selectedParty?.name ?? "") || [];
        const isCommendARSSubscribed = items.includes("Commend (ARS)");

        if (!isCommendARSSubscribed) return [];

        const convertedItems: TAppSubMenuItem[] = Object.entries(ARSBoxData).map(([key, value]) => ({
            title: value,
            value: key,
            checked: false
        }));
        setCommendSubMenuItems(convertedItems);
        return convertedItems;
    }

    const fetchSubMenuItems = async () => {
        if (!industryId) return Promise.resolve([]);

        try {
            setPagination((prev: TPaginationParamsWithLoading) => ({ ...prev, loading: true }));
            if (!selectedMerchant?.merchant_key) {
                clearSubmenuData();
                return [];
            }
            const responseData = await getISRProjectsForAnalytics(industryId, selectedMerchant?.merchant_key);

            if (responseData?.done) {
                const formattedItems = (responseData.data ?? []).map((item: any) => ({
                    title: item.ISRName,
                    value: item.id,
                    checked: false
                }));

                setSubMenuItems((prev: TAppSubMenuItem[]) => {
                    const existingIds = new Set(prev.map(item => item.value));
                    const uniqueNewItems = formattedItems.filter((item: TAppSubMenuItem) => !existingIds.has(item.value));
                    return [...prev, ...uniqueNewItems];
                });

                setPagination((prev: TPaginationParamsWithLoading) => ({
                    ...prev,
                    hasMore: responseData.total > 0 && (prev.pageNumber * prev.pageSize) < responseData.total
                }));

                return formattedItems;
            } else {
                clearSubmenuData();
                return [];
            }
        } catch (error) {
            clearSubmenuData();
            console.error('Error fetching submenu items:', error);
            return [];
        } finally {
            setPagination((prev: TPaginationParamsWithLoading) => ({ ...prev, loading: false }));
        }
    };

    // Set initial date range based on default selection
    useEffect(() => {
        const dateRange = getDateRangeBasedTimeGrain(LAST_30_DAYS, "UTC");
        setPayload(prev => ({ ...prev, dateRange }));

        // Also set the formatted display value
        const today = moment().tz("UTC");
        const startDate = today.clone().subtract(30, 'days').toDate();
        const endDate = today.toDate();
        const formattedRange = formatDateRange(startDate, endDate, LAST_30_DAYS, "UTC");

        setSelectedItem(prev => ({ ...prev, value: formattedRange }));
    }, []);

    useEffect(() => {
        fetchSubMenuItems();
    }, [pagination.pageNumber]);

    useEffect(() => {
        setMenuData(menuItems)
        clearSubmenuData();
        fetchSubMenuItems();
        fetchCommendSubMenuItems();
    }, [selectedMerchant]);

    useEffect(() => {
        ensureDefaultApplication();
    }, [payload.applications]);

    const ensureDefaultApplication = (): boolean => {
        if (!payload.applications?.length) {
            setPayload((prev: TAnalyticsPayload) => ({
                ...prev,
                applications: ["Compatio"]
            }));
            return true;
        }
        return false;
    };

    const clearAllTheFields = () => {
        setMerchantData([]);
        setSelectedMerchant(undefined);
        setSelectedCompareMenuItem("None");
        setMenuData(menuItems);
        setComingFromDetailPage(false);

        // Reset to default values instead of empty values
        setPayload({
            granularity: P1D,
            merchant_key: [],
            dateRange: getDateRangeBasedTimeGrain(LAST_30_DAYS, "UTC"),
            compareTo: 'None',
            applications: ["Compatio"],
            timezone: 'UTC',
            builder_ids: [],
            box_ids: []
        });

        setDashedArray([]);
        clearGraphAndMetricsData();
        setSelectedTimezone({ name: "UTC", abbreviation: "UTC" });

        // Reset to default selected item
        setSelectedItem({ label: LAST_30_DAYS, value: getInitialDateRangeDisplay() });

        clearSubmenuData();
    }

    const clearSubmenuData = () => {
        setSubMenuItems([]);
        setSelectedSubMenuItems([]);
        setCommendSubMenuItems([]);
        setSelectedCommendSubMenuItems([]);
        setPagination({
            pageNumber: 1,
            pageSize: 10,
            hasMore: false,
            loading: false
        });
    }

    const clearGraphAndMetricsData = () => {
        setApplicationMetrics({ data: [] });
        setGraphData([]);
    }

    const setDateRange = (timeZoneName: string) => {
        let dateRange = getDateRangeBasedTimeGrain(selectedItem?.label, timeZoneName);
        setPayload({ ...payload, dateRange: dateRange })
    }

    const fetchGraphData = async () => {

        try {
            setShowGraphLoader(true);
            ensureDefaultApplication();
            setActiveRequestCount(prev => prev + 1);
            let dateRange = getDateRangeBasedTimeGrain(selectedItem?.label, selectedTimezone?.name);
            if (selectedCompareMenuItem !== NONE) {
                let preDateRange = [];
                if (selectedItem.label === THIS_YEAR_1M || selectedItem.label === THIS_YEAR_1W) {
                    const { startOfYear, endOfYear } = getStartAndEndOfPreviousYear(selectedTimezone?.name);
                    preDateRange = [formatDate(startOfYear, selectedTimezone?.name), upperBoundDateformat(endOfYear, selectedTimezone?.name)]
                } else if (selectedItem.label === TODAY) {
                    preDateRange = calculatePreviousPeriod(dateRange, payload.granularity, selectedItem.label, selectedTimezone?.name);
                    dateRange = [getDateWithMidnight(selectedTimezone?.name), getFormattedDateTime(selectedTimezone?.name)];
                } else {
                    preDateRange = calculatePreviousPeriod(dateRange, payload.granularity, selectedItem.label, selectedTimezone?.name);
                }
                const updateState = { ...payload, dateRange: preDateRange };
                const updateStateCP = { ...payload, dateRange: dateRange };
                const currentPeriodData = await fetchAllGraphData(updateStateCP);
                if (currentPeriodData === null) {
                    return;
                }
                const previousPeriodData = await fetchAllGraphData(updateState)
                if (previousPeriodData === null) {
                    return;
                }
                const dashedArray = calculateDashArray(true, payload.applications.length, payload.applications.length);
                const convertedResponse = combineApiResponseToGraph(currentPeriodData, previousPeriodData, payload.granularity);
                setDashedArray(dashedArray);
                setGraphData(convertedResponse);
            } else {
                let updatedDateRange = dateRange;
                if (selectedItem.label === TODAY) {
                    updatedDateRange = [getDateWithMidnight(selectedTimezone?.name), getFormattedDateTime(selectedTimezone?.name)];
                } else if ([THIS_YEAR_1M, THIS_YEAR_1W, THIS_YEAR_2W].includes(selectedItem.label)) {
                    updatedDateRange = adjustDateRangeWithCurrentTime(payload.dateRange, selectedTimezone?.name);
                }
                const updateState = { ...payload, dateRange: updatedDateRange };
                const response = await fetchAllGraphData(updateState);
                if (response === null) {
                    setGraphData([]);
                    return;
                }
                const dashedArray = calculateDashArray(false, payload.applications.length, 0);
                const result = convertApiResponseToGraph(response, payload.granularity);
                setDashedArray(dashedArray);
                setGraphData(result);
            }
        } catch (error) {
            setShowGraphLoader(false);
            console.error("fetchGraphData ", error);
        } finally {
            setShowGraphLoader(false);
        }
    }

    return (
        <AnalyticsContext.Provider value=
            {{
                payload,
                setPayload,
                selectedItem,
                setSelectedItem,
                selectedCompareMenuItem,
                setSelectedCompareMenuItem,
                menuData,
                setMenuData,
                merchantData,
                setMerchantData,
                selectedMerchant,
                setSelectedMerchant,
                clearAllTheFields,
                applicationMetrics,
                setApplicationMetrics,
                showLoader,
                setShowLoader,
                showGraphLoader,
                setShowGraphLoader,
                graphData,
                setGraphData,
                comingFromDetailPage,
                setComingFromDetailPage,
                dashedArray,
                setDashedArray,
                paginationParams,
                setPaginationParams,
                fetchGraphData,
                setSelectedTimezone,
                selectedTimezone,
                menuItems,
                clearGraphAndMetricsData,
                selectedRange,
                setSelectedRange,
                setDateRange,
                subMenuItems,
                setSubMenuItems,
                pagination,
                setPagination,
                fetchSubMenuItems,
                openSubmenu,
                setOpenSubmenu,
                selectedSubMenuItems,
                setSelectedSubMenuItems,
                commendSubMenuItems,
                setCommendSubMenuItems,
                selectedCommendSubMenuItems,
                setSelectedCommendSubMenuItems,
                fetchCommendSubMenuItems,
                ensureDefaultApplication
            }}>
            {children}
        </AnalyticsContext.Provider>
    );
}
